import React, { useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import './SafeExternalLink.css';
import { getDomainRiskLevel, assessSecurityContext, SAFE_LINK_CONFIG } from './safeLinksConfig';

// Security Context Hook - integrates with your Redux store
const useSecurityContext = () => {
    const client = useSelector(state => 
        state?.summary?.localClient || state?.summary?.clients?.data?.[0]
    );
    
    // Get subdomain discovery data
    const subdomainData = useSelector(state => 
        state?.subDomain?.subdomainDnsDetails?.data?.data || []
    );
    
    // Get look-alike domain data (adjust path based on your Redux store structure)
    const lookAlikeData = useSelector(state => 
        state?.lookAlike?.data || state?.lookAlike?.domains || []
    );
    
    // Get SSL certificate data - COMMENTED OUT
    // const sslCertData = useSelector(state => 
    //     state?.subDomain?.responseexp?.data || []
    // );
    
    // Get web technology data - COMMENTED OUT
    // const webTechData = useSelector(state => 
    //     state?.subDomain?.response?.data?.technologies || []
    // );
    
    // Build security context from your existing data
    const getAllTrustedDomains = () => {
        const domains = new Set();
        
        // Add main client domains
        (client?.domains || []).forEach(domain => domains.add(domain));
        
        // Add client website
        const website = client?.website?.replace(/^(?:https?:\/\/)?(?:www\.)?/i, "")?.replace(/\/$/, "");
        if (website) domains.add(website);
        
        // Add all non-dangling subdomains as trusted
        subdomainData
            .filter(item => item?.isDangling !== true)
            .map(item => item?.subDomainName)
            .filter(Boolean)
            .forEach(subdomain => {
                domains.add(subdomain);
                
                // Also add root domain if subdomain is provided
                // e.g., if "api.website.no" is trusted, also trust "website.no"
                const parts = subdomain.split('.');
                if (parts.length > 2) {
                    const rootDomain = parts.slice(-2).join('.');
                    domains.add(rootDomain);
                }
            });
        
        return Array.from(domains).filter(domain => domain && domain.trim());
    };

    const securityContext = {
        trustedDomains: getAllTrustedDomains(),
        
        // Store dangling subdomains for high-risk assessment
        danglingSubdomains: subdomainData
            .filter(item => item?.isDangling === true)
            .map(item => item?.subDomainName)
            .filter(Boolean),
            
        // Store look-alike domain data for sophisticated spoofing detection
        lookAlikeDomains: lookAlikeData || []
        
        // SSL and Web Technology checks - COMMENTED OUT
        // sslCertificates: sslCertData || [],
        // webTechnologies: webTechData || []
    };
    
    return securityContext;
};

const SafeExternalLink = ({ 
    url, 
    children, 
    className = "", 
    style = {},
    forceWarning = false,
    linkText = null,
    showRiskIndicator = true
}) => {
    const [showWarning, setShowWarning] = useState(false);
    const securityContext = useSecurityContext();
    const riskLevel = getDomainRiskLevel(url, securityContext);
    
    // Enhanced security assessment
    const securityAssessment = url ? assessSecurityContext(
        new URL(url.startsWith('http') ? url : `https://${url}`).hostname.toLowerCase(),
        securityContext
    ) : null;
    
    // Debug logging for domain issues
    if (url && (url.includes('website.no') || url.includes('helpv2.website.no') || url.includes('pharmacist-portal.website.no'))) {
        console.log('=== DOMAIN DEBUG ===');
        console.log('URL:', url);
        console.log('Domain:', url ? new URL(url.startsWith('http') ? url : `https://${url}`).hostname.toLowerCase() : null);
        console.log('Security Context:', securityContext);
        console.log('Risk Level:', riskLevel);
        console.log('Security Assessment:', securityAssessment);
        console.log('===================');
    }
    
    // Show warning for medium/high risk or if forced
    const shouldShowWarning = forceWarning || riskLevel === 'high' || riskLevel === 'medium';

    // URL validation and sanitization (unchanged)
    const isUrlSafe = (url) => {
        try {
            const urlObj = new URL(url);
            const protocol = urlObj.protocol.toLowerCase();
            
            // Check for dangerous protocols - using individual checks to avoid linting issues
            if (protocol.startsWith('javascript') || 
                protocol.startsWith('data') || 
                protocol.startsWith('vbscript') || 
                protocol.startsWith('file') || 
                protocol.startsWith('ftp')) {
                return false;
            }
            
            // Only allow HTTP and HTTPS
            if (protocol !== 'http:' && protocol !== 'https:') {
                return false;
            }
            return true;
        } catch (e) {
            return false;
        }
    };

    const sanitizeUrl = (url) => {
        if (!url) return '#';
        if (!url.match(/^https?:\/\//)) {
            url = 'https://' + url;
        }
        return isUrlSafe(url) ? url : '#';
    };

    const formatUrlForDisplay = (url, maxLength = 50) => {
        if (!url) return 'Invalid URL';
        if (url.length <= maxLength) return url;
        return url.substring(0, maxLength - 3) + '...';
    };

    const safeUrl = sanitizeUrl(url);

    const handleClick = (e) => {
        e.preventDefault();
        if (safeUrl === '#') {
            alert('This URL appears to be malformed or potentially dangerous.');
            return;
        }
        if (shouldShowWarning) {
            setShowWarning(true);
        } else {
            proceedToLink();
        }
    };

    const proceedToLink = () => {
        window.open(safeUrl, '_blank', 'noopener,noreferrer');
        setShowWarning(false);
    };

    // Dynamic styling based on risk level
    const getRiskColor = () => {
        switch(riskLevel) {
            case 'high': return '#dc3545'; // Red
            case 'medium': return '#ffc107'; // Yellow
            case 'low': return '#28a745'; // Green
            default: return '#ffffff'; // White
        }
    };

    const getRiskIcon = () => {
        switch(riskLevel) {
            case 'high': return 'fa-exclamation-triangle';
            case 'medium': return 'fa-exclamation-circle';
            case 'low': return 'fa-check-circle';
            default: return 'fa-external-link';
        }
    };

    const linkStyle = {
        color: getRiskColor(),
        textDecoration: 'none',
        cursor: 'pointer',
        ...style
    };

    return (
        <>
            <span className={`safe-external-link ${className}`} style={{ position: 'relative' }}>            
                <a 
                    href={safeUrl} 
                    onClick={handleClick}
                    style={linkStyle}
                    title={`External link: ${url} - Risk Level: ${riskLevel.toUpperCase()}${securityAssessment?.reason ? ' - ' + securityAssessment.reason : ''}`}
                    className={`risk-${riskLevel}`}
                >
                    {children || linkText || formatUrlForDisplay(url)}
                </a>
                
                {showRiskIndicator && (
                    <small className="ml-1">
                        <i className={`fa ${getRiskIcon()} text-${riskLevel === 'high' ? 'danger' : riskLevel === 'medium' ? 'warning' : 'success'}`}
                            title={`Risk Level: ${riskLevel.toUpperCase()}${securityAssessment?.reason ? ' - ' + securityAssessment.reason : ''}`}></i>
                    </small>
                )}
            </span>

            {/* Enhanced Warning Modal with Security Context */}
            {showWarning && (
                <div className="modal fade show d-block safe-link-modal" style={{ backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 9999 }}>
                    <div className="modal-dialog modal-md">
                        <div className="modal-content">
                            <div className={`modal-header ${riskLevel === 'high' ? 'bg-danger text-white' : 'bg-warning text-dark'}`}>
                                <h5 className="modal-title">
                                    <i className={`fa ${getRiskIcon()} mr-2`}></i>
                                    External Link Security Warning - {riskLevel.toUpperCase()} Risk
                                </h5>
                                <button 
                                    type="button" 
                                    className={`close ${riskLevel === 'high' ? 'text-white' : 'text-dark'}`}
                                    onClick={() => setShowWarning(false)}
                                >
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className={`alert ${riskLevel === 'high' ? 'alert-danger' : 'alert-warning'}`}>
                                    <strong>
                                        {riskLevel === 'high' ? '🚨 HIGH RISK: This website may be dangerous!' : '⚠️ MEDIUM RISK: Exercise caution when visiting this website'}
                                    </strong>
                                </div>
                                
                                <div className="mb-3">
                                    <strong>Destination URL:</strong>
                                    <div className="mt-2 p-3 bg-light rounded" 
                                        style={{ 
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-all',
                                            border: '1px solid #dee2e6'
                                        }}>
                                        {safeUrl}
                                    </div>
                                </div>

                                {/* Enhanced Risk Analysis with Security Context */}
                                <div className="mb-3">
                                    <strong>Risk Analysis:</strong>
                                    <EnhancedRiskAnalysis 
                                        url={safeUrl} 
                                        riskLevel={riskLevel} 
                                        securityAssessment={securityAssessment}
                                    />
                                </div>

                                <div className={`alert ${riskLevel === 'high' ? 'alert-danger' : 'alert-warning'}`}>
                                    <h6><strong>🛡️ Security Recommendations:</strong></h6>
                                    <ul className="mb-0">
                                        <li><strong>Do NOT enter any personal information</strong> (passwords, emails, etc.)</li>
                                        <li><strong>Do NOT download any files</strong> from this site</li>
                                        <li><strong>Do NOT install any software</strong> or browser extensions</li>
                                        <li><strong>Close the tab immediately</strong> if the site looks suspicious</li>
                                        <li><strong>Use a sandboxed browser</strong> or virtual machine if available</li>
                                        {riskLevel === 'high' && (
                                            <li className="text-danger"><strong>Consider NOT visiting this site at all</strong></li>
                                        )}
                                    </ul>
                                </div>

                                <div className="alert alert-info">
                                    <strong>💡 Tip:</strong> This link opens in a new tab with security restrictions applied.
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => setShowWarning(false)}
                                >
                                    <i className="fa fa-times mr-1"></i>
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className={`btn ${riskLevel === 'high' ? 'btn-danger' : 'btn-warning'}`}
                                    onClick={proceedToLink}
                                >
                                    <i className="fa fa-external-link mr-1"></i>
                                    {riskLevel === 'high' ? 'I Accept the Risk - Proceed' : 'I Understand - Proceed'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

// Enhanced Risk Analysis Component with Security Context
const EnhancedRiskAnalysis = ({ url, riskLevel, securityAssessment }) => {
    const analysis = [];
    
    // Add security context analysis first (highest priority)
    if (securityAssessment && securityAssessment.level !== 'unknown') {
        const icon = securityAssessment.level === 'high' ? '🔴' : 
                    securityAssessment.level === 'medium' ? '🟡' : '🟢';
        
        // Special handling for look-alike domain attacks
        if (securityAssessment.attackType) {
            const attackTypeEmojis = {
                'Homoglyph': '👁️',
                'TLD-swap': '🔄',
                'Replacement': '🔤',
                'Subdomain': '🌐',
                'Addition': '➕',
                'Omission': '➖',
                'Hyphenation': '➖',
                'Dictionary': '📖',
                'Various': '⚠️'
            };
            
            const emoji = attackTypeEmojis[securityAssessment.attackType] || '⚠️';
            analysis.push(`${icon} ${emoji} **${securityAssessment.attackType} Attack Detected**`);
            analysis.push(`   🎯 Target: ${securityAssessment.similarDomain}`);
            analysis.push(`   ⚠️ ${securityAssessment.reason}`);
            
            // Add explanation in a more readable format
            if (securityAssessment.details) {
                const parts = securityAssessment.details.split(' - ');
                if (parts.length > 1) {
                    analysis.push(`   💡 ${parts[parts.length - 1]}`);
                }
            }
        } else {
            analysis.push(`${icon} ${securityAssessment.reason}`);
            if (securityAssessment.details) {
                analysis.push(`   ℹ️ ${securityAssessment.details}`);
            }
        }
    }
    
    try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.toLowerCase();
        
        // Check against patterns
        SAFE_LINK_CONFIG.dangerousPatterns.forEach(pattern => {
            if (pattern.pattern.test(domain)) {
                const icon = pattern.risk === 'high' ? '🔴' : '🟡';
                analysis.push(`${icon} ${pattern.description}`);
            }
        });
        
        // Check file extensions
        SAFE_LINK_CONFIG.riskyExtensions.forEach(ext => {
            if (url.toLowerCase().includes(ext)) {
                analysis.push(`🔴 Potentially dangerous file type: ${ext}`);
            }
        });
        
    } catch (e) {
        analysis.push('🔴 Malformed URL structure');
    }
    
    if (analysis.length === 0) {
        analysis.push('🟡 Unknown domain - proceed with caution');
    }
    
    return (
        <ul className="mb-0 small">
            {analysis.map((item, index) => (
                <li key={index}>{item}</li>
            ))}
        </ul>
    );
};

export default SafeExternalLink;